import { useState } from "react";
import { format } from "date-fns";
import {
  Autocomplete as MuiAutocomplete,
  Button as MuiButton,
  CircularProgress as MuiCircularProgress,
  IconButton as MuiIconButton,
  Link as MuiLink,
  Stack as MuiStack,
  TextField as MuiTextField,
  Typography as MuiTypography,
  Tooltip as MuiTooltip,
} from "@mui/material";
import { Info as MuiInfoIcon } from "@mui/icons-material";
import {
  DatePicker as MuiDatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFnsV3";
import { Trans, useTranslation } from "react-i18next";
import { Theme } from "common";
import StudyRecruitmentDialog from "./StudyRecruitmentDialog";
import InfoDialog from "app/shared/UI/Dialog/InfoDialog";
import ConfirmationDialog from "app/shared/UI/Dialog/ConfirmationDialog";
import { useStudyRecruitment } from "app/services/StudyRecruitmentService/StudyRecruitmentProvider";
import {
  COHORT_STUDY_RECRUITMENT_STATUS,
  LOADER_TYPES,
  OUTREACH_EMAIL,
} from "app/services/StudyRecruitmentService/constants";

const boxStyles = {
  padding: "16px",
  backgroundColor: Theme.palette.primary.contrastText,
  borderRadius: "8px",
  boxShadow: "2px 10px 10px rgba(0, 0, 0, 0.1)",
  width: "834px",
  border: "1px solid #C4C4C4",
};

const labelStyles = {
  fontSize: "14px",
  fontWeight: 700,
  lineHeight: "18px",
};

const infoIconStyles = {
  button: { p: 0 },
  icon: { width: "18px", height: "18px" },
};

const buttonStyles = { width: "120px", height: "40px" };

const StudyRecruitmentOutreach = () => {
  const { t } = useTranslation();
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [pendingCohortAction, setPendingCohortAction] = useState(null);
  const [pendingCohortResumeDate, setPendingCohortResumeDate] = useState(null);
  const {
    studyRecruitmentMetrics,
    cohorts,
    selectedCohort,
    setSelectedCohort,
    resumeActivationDate,
    setResumeActivationDate,
    updateOutreachCohortStatus,
    isLoading,
  } = useStudyRecruitment();
  const { START, STOP, PAUSE, RESUME, NOT_STARTED, ABORTED } =
    COHORT_STUDY_RECRUITMENT_STATUS;
  const actionsDisabled = !studyRecruitmentMetrics.outreachEnabled;
  const startButtonDisabled =
    actionsDisabled || Number(selectedCohort?.availableProspects) === 0;
  const loading = isLoading[LOADER_TYPES.UPDATE_OUTREACH_COHORT_STATUS];

  const getCohortActionButtons = (cohortStatus) => {
    switch (cohortStatus) {
      case NOT_STARTED:
      case ABORTED:
      case STOP:
        return (
          <MuiButton
            variant="contained"
            disabled={startButtonDisabled || actionsDisabled || loading}
            onClick={() => {
              setPendingCohortAction(START);
              setShowConfirmationDialog(true);
            }}
            sx={buttonStyles}
          >
            {loading ? (
              <MuiCircularProgress
                color="inherit"
                size="20px"
                sx={{ mx: "1em" }}
              />
            ) : (
              <MuiTypography sx={{ fontSize: "16px" }}>
                {t("buttons.caps.start")}
              </MuiTypography>
            )}
          </MuiButton>
        );
      case START:
      case RESUME:
        return (
          <MuiStack direction="row" gap={1.25}>
            <MuiButton
              variant="outlined"
              disabled={startButtonDisabled || actionsDisabled || loading}
              onClick={() => {
                setPendingCohortAction(PAUSE);
                setShowPauseDialog(true);
              }}
              sx={buttonStyles}
            >
              {loading ? (
                <MuiCircularProgress
                  color="inherit"
                  size="20px"
                  sx={{ mx: "1em" }}
                />
              ) : (
                <MuiTypography sx={{ fontSize: "16px" }}>
                  {t("buttons.caps.pause")}
                </MuiTypography>
              )}
            </MuiButton>
            <MuiButton
              variant="contained"
              disabled={actionsDisabled || loading}
              onClick={() => {
                setPendingCohortAction(STOP);
                setShowConfirmationDialog(true);
              }}
              sx={buttonStyles}
            >
              {loading ? (
                <MuiCircularProgress
                  color="inherit"
                  size="20px"
                  sx={{ mx: "1em" }}
                />
              ) : (
                <MuiTypography sx={{ fontSize: "16px" }}>
                  {t("buttons.caps.stop")}
                </MuiTypography>
              )}
            </MuiButton>
          </MuiStack>
        );
      case PAUSE:
        return (
          <MuiStack direction="row" gap={1.25}>
            <MuiButton
              variant="contained"
              disabled={actionsDisabled || loading}
              onClick={() => {
                setPendingCohortAction(RESUME);
                setShowConfirmationDialog(true);
              }}
              sx={buttonStyles}
            >
              {loading ? (
                <MuiCircularProgress
                  color="inherit"
                  size="20px"
                  sx={{ mx: "1em" }}
                />
              ) : (
                <MuiTypography sx={{ fontSize: "16px" }}>
                  {t("buttons.caps.resume")}
                </MuiTypography>
              )}
            </MuiButton>
            <MuiButton
              variant="contained"
              disabled={actionsDisabled || loading}
              onClick={() => {
                setPendingCohortAction(STOP);
                setShowConfirmationDialog(true);
              }}
              sx={buttonStyles}
            >
              {loading ? (
                <MuiCircularProgress
                  color="inherit"
                  size="20px"
                  sx={{ mx: "1em" }}
                />
              ) : (
                <MuiTypography sx={{ fontSize: "16px" }}>
                  {t("buttons.caps.stop")}
                </MuiTypography>
              )}
            </MuiButton>
          </MuiStack>
        );
      default:
        break;
    }
  };

  const handlePauseDialogClose = async (choice) => {
    if (choice) {
      setShowConfirmationDialog(true);
    }

    setShowPauseDialog(false);
  };

  const handleConfirmationDialogClose = async (choice) => {
    setShowConfirmationDialog(false);
    setPendingCohortAction(null);
    setPendingCohortResumeDate(null);

    if (choice) {
      if (pendingCohortAction === PAUSE) {
        await updateOutreachCohortStatus(
          selectedCohort.id,
          PAUSE,
          pendingCohortResumeDate,
        );
      } else {
        await updateOutreachCohortStatus(
          selectedCohort.id,
          pendingCohortAction,
        );
      }
    }
  };

  return (
    <MuiStack gap={1}>
      <MuiStack alignItems="center" direction="row" gap={0.5}>
        <MuiTypography
          sx={{ fontWeight: 600, color: "black", fontSize: "20px" }}
        >
          {t("studyRecruitment.sectionTitles.outreach")}
        </MuiTypography>
        <MuiTooltip
          title={t("studyRecruitment.tooltips.info")}
          placement="bottom"
        >
          <MuiIconButton
            onClick={() => setOpenInfoDialog(true)}
            sx={infoIconStyles.button}
          >
            <MuiInfoIcon sx={infoIconStyles.icon} />
          </MuiIconButton>
        </MuiTooltip>
      </MuiStack>
      <MuiStack gap={1} sx={boxStyles}>
        {resumeActivationDate && (
          <MuiStack
            alignItems="center"
            justifyContent="flex-end"
            direction="row"
            gap={0.5}
          >
            <MuiTypography sx={labelStyles}>
              {t("studyRecruitment.labels.resumeDate")}:
            </MuiTypography>
            <MuiTypography sx={{ fontSize: "14px", lineHeight: "18px" }}>
              {format(new Date(resumeActivationDate), "MM/dd/yyyy")}
            </MuiTypography>
          </MuiStack>
        )}

        <MuiStack direction="row" justifyContent="space-between">
          <MuiAutocomplete
            id="pep-study-recruitment-outreach-cohort-autocomplete"
            options={cohorts}
            value={selectedCohort || null}
            onChange={(e, val) => {
              setSelectedCohort(val);
              setResumeActivationDate(val?.studyTeamOutreachResumeDate);
            }}
            getOptionLabel={(option) => option.name?.replaceAll("_", " ") || ""}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            openOnFocus
            disableClearable
            // disabled={actionsDisabled}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                label={t(`studyRecruitment.labels.cohort`)}
                variant="outlined"
                size="small"
                error={selectedCohort?.studyTeamOutreachStatus === ABORTED}
                helperText={
                  selectedCohort?.studyTeamOutreachStatus === ABORTED ? (
                    <Trans
                      i18nKey="ConductOutreachPage.errorMessageForUniqueWaveNameStudyTeamOutreachAborted"
                      components={{
                        linkEmail: (
                          <MuiLink
                            key="email"
                            href={OUTREACH_EMAIL}
                            sx={{
                              color: Theme.palette.primary.dark,
                              cursor: "pointer",
                              textDecoration: "none",
                            }}
                          />
                        ),
                      }}
                    />
                  ) : null
                }
              />
            )}
            renderOption={(props, option) => (
              <div style={{ fontSize: "14px" }} {...props} key={option.id}>
                {option.name?.replaceAll("_", " ")}
              </div>
            )}
            sx={{
              width: "365px",
              "& .MuiAutocomplete-endAdornment .MuiIconButton-root": {
                // Move dropdown arrow and clear X up to horizontal center
                height: "auto",
              },
              "& .MuiAutocomplete-popupIndicatorOpen": {
                // Stop MUI from rotating the icon by rotating it back
                rotate: "180deg",
              },
              "& .MuiInputLabel-root, .MuiInputBase-input": {
                fontSize: "14.5px",
              },
            }}
          />

          {getCohortActionButtons(selectedCohort?.studyTeamOutreachStatus)}
        </MuiStack>
      </MuiStack>

      <StudyRecruitmentDialog
        open={showPauseDialog}
        onClose={handlePauseDialogClose}
        title={t("studyRecruitment.labels.studyTeamOutreachPause")}
        okLabel={t("buttons.caps.submit")}
        cancelLabel={t("buttons.caps.cancel")}
        disabledOkButton={!pendingCohortResumeDate}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <MuiDatePicker
            label={t("studyRecruitment.labels.resumeDate")}
            value={
              pendingCohortResumeDate ? new Date(pendingCohortResumeDate) : null
            }
            onChange={(date) => setPendingCohortResumeDate(date)}
            slotProps={{
              textField: {
                fullWidth: true,
                size: "small",
              },
            }}
          />
        </LocalizationProvider>
      </StudyRecruitmentDialog>

      <InfoDialog
        open={openInfoDialog}
        handleClose={() => setOpenInfoDialog(false)}
        title={t("studyRecruitment.infoDialog.studyTeamOutreach.title")}
      >
        <MuiTypography
          sx={{ fontSize: "14px", lineHeight: "18px", whiteSpace: "pre-wrap" }}
        >
          <Trans
            i18nKey="studyRecruitment.infoDialog.studyTeamOutreach.content"
            components={{
              linkEmail: (
                <MuiLink
                  key="email"
                  href={OUTREACH_EMAIL}
                  sx={{
                    color: Theme.palette.primary.dark,
                    cursor: "pointer",
                    textDecoration: "none",
                  }}
                />
              ),
            }}
          />
        </MuiTypography>
      </InfoDialog>

      <ConfirmationDialog
        open={showConfirmationDialog}
        onClose={handleConfirmationDialogClose}
        message={t("studyRecruitment.confirmationDialog.updateCohortOutreach")}
        cancelLabel={t("buttons.caps.cancel")}
      />
    </MuiStack>
  );
};

export default StudyRecruitmentOutreach;
